import { Badge, Box } from "@mui/material";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { BADGE, STATUS, SURFACE } from "../../../theme/tokens";
import { InHouseTooltip } from "../../InHouseTooltip/InHouseTooltip";

type Props = {
	/** True while the filter hides something. */
	active: boolean;
	children: ReactNode;
	dataTestId: string;
};

/**
 * An orange dot on a filter control's top-right corner while the filter is narrowed. A closed dropdown shows
 * only a truncated value, so without it nothing says the grid is hiding rows.
 *
 * A `standard` badge shrunk to a dot rather than `variant="dot"`: the dot variant renders no content, so there is
 * nothing to hang the hover tooltip on. Still MUI's badge, so it keeps its show/hide animation.
 */
export const FilterActiveDot = ({ active, children, dataTestId }: Props) => {
	const { t } = useTranslation();
	return (
		<Badge
			invisible={!active}
			anchorOrigin={{ vertical: "top", horizontal: "right" }}
			data-testid={dataTestId}
			badgeContent={
				<InHouseTooltip title={t("filter.active.tooltip")}>
					<Box
						component="span"
						role="img"
						aria-label={t("filter.active.tooltip")}
						sx={{ display: "block", width: "100%", height: "100%", borderRadius: "50%" }}
					/>
				</InHouseTooltip>
			}
			sx={(theme) => ({
				"& .MuiBadge-badge": {
					// Inset from the corner so the dot sits on the control rather than floating off its rounded edge.
					// Offset, not a transform override: MUI hides the badge by scaling its transform.
					top: theme.spacing(0.9),
					right: theme.spacing(0.9),
					width: BADGE.DOT_SIZE,
					height: BADGE.DOT_SIZE,
					minWidth: BADGE.DOT_SIZE,
					padding: 0,
					borderRadius: "50%",
					// Orange, not brand: it has to stand out against the brand-coloured controls around it.
					backgroundColor: STATUS.WARNING.main,
					boxShadow: `0 0 0 2px ${SURFACE}`,
				},
			})}
		>
			{children}
		</Badge>
	);
};
